import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { styled, Box, Typography, Stack, Container, useTheme, alpha, IconButton, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import menu2Fill from "@iconify/icons-eva/menu-2-fill";

//
import DashboardNavbar from "./DashboardNavbar";
import DashboardSidebar from "./DashboardSidebar";
import AccountPopover from "./AccountPopover";
import { useDashboardTitle } from "./useDashboardTitle";

import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";
import { is } from "src/util/is";
import IntercomWidget from "src/common/components/IntercomWidget";
import Footer from "src/components/common/Footer";
// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled("div")({
  display: "flex",
  minHeight: "100%",
  overflow: "hidden",
});

const MainStyle = styled("div")(({ theme }) => ({
  flexGrow: 1,
  overflow: "hidden",
  minHeight: "100vh",
  paddingTop: 10,
  paddingBottom: theme.spacing(2),
  width: "100%",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingTop: 10,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const team = useSelectedTeam();
  const theme = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const pageTitle = useDashboardTitle();
  
  // Check if current page is dashboard
  const isDashboardPage = location.pathname === '/dashboard' || location.pathname === '/';

  /**
   * First thing to call after login or page refresh
   * This function will check if JWT token is set or not
   * If not it will redirect user to login
   * If yes, fetch JWT token from local storage and parse it. set userId to state
   */
  // useEffect(() => {
  //   const userData = Token.parsedToken();
  //   if (userData && userData.success && userData.data) {
  //     setUsername(userData.data.username);
  //     setUserId(userData.data._id);
  //   } else {
  //     navigate("/login");
  //   }
  // }, []);

  /**
   * First thing to call after login or page refresh
   * return axios.get("/user/getUserDetail", {
      params: {
        username: userData.data.username
      }
   */
  // useEffect(() => {
  //   if (!username) {
  //     return;
  //   }
  //   (async function () {
  //     try {
  //       const getUserDetail = async () => {
  //         const response = await axios(
  //           `${process.env.REACT_APP_SERVER_URL}user/${userId}`,
  //           {
  //             headers: {
  //               "Content-Type": "application/json",
  //               Authorization: `Bearer ${Token.get()}`,
  //             },
  //           }
  //         );
  //         const userDetailRes = response.data.data;
  //         if (userDetailRes) {
  //           setUserDetail(userDetailRes);
  //         }
  //       };

  //       await getUserDetail();
  //     } catch (error) {
  //       console.error(error.response);
  //       navigate("/login");
  //     }
  //   })();
  // }, [username]);

  // useEffect(() => {
  //   if (userDetail && userDetail._id) {
  //     setIsAuthenticated(true);
  //   }
  // }, [userDetail]);
  return (
    <RootStyle>
      <DashboardSidebar
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
      />
      <MainStyle>
        {!is.empty(team) && (
          <>
            {/* Common Header for all pages */}
            <Container
              maxWidth="xl"
              sx={{ 
                "@media screen and (min-width:600px)": { padding: 0 },
                width: '100%',
                maxWidth: '100% !important',
                overflow: 'hidden',
                px: { xs: 1, sm: 2, md: 3 }
              }}
            >
              <Box 
                sx={{ 
                  background: `linear-gradient(135deg, #1A73E8 0%, #34A853 50%, #0D47A1 100%)`,
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  mb: 2,
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `0 8px 32px ${alpha('#1A73E8', 0.4)}`,
                  border: `1px solid ${alpha('#4CAF50', 0.3)}`,
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(45deg, transparent 30%, ${alpha('#ffffff', 0.15)} 50%, transparent 70%)`,
                    // Animation removed
                  },
                  // Keyframes animation removed
                }}
              >
                {/* Enhanced Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '300px',
                    height: '300px',
                    background: `radial-gradient(circle, ${alpha('#ffffff', 0.2)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(30%, -30%)',
                    filter: 'blur(1px)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '200px',
                    height: '200px',
                    background: `radial-gradient(circle, ${alpha('#ffffff', 0.15)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(-30%, 30%)',
                    filter: 'blur(1px)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100px',
                    height: '100px',
                    background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 70%)`,
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    filter: 'blur(2px)',
                  }}
                />
                
                <Stack
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={{ xs: 1, sm: 0 }}
                  sx={{ position: 'relative', zIndex: 1 }}
                >
                  {/* Mobile Menu Button - Only visible on mobile */}
                  <Box sx={{ display: { xs: 'block', lg: 'none' }, mr: 2, mb: { xs: 1, sm: 0 } }}>
                    <IconButton
                      onClick={() => setOpen(true)}
                      sx={{ 
                        color: 'white',
                        background: `linear-gradient(135deg, ${alpha('#ffffff', 0.2)} 0%, ${alpha('#ffffff', 0.1)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        p: 1,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#ffffff', 0.3)} 0%, ${alpha('#ffffff', 0.2)} 100%)`,
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <Icon icon={menu2Fill} width={20} height={20} />
                    </IconButton>
                  </Box>

                  {/* Left Section - Title and Welcome */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700,
                        mb: 0.25,
                        fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
                        background: 'linear-gradient(45deg, #ffffff 0%, #e3f2fd 50%, #bbdefb 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        letterSpacing: '-0.25px',
                      }}
                    >
                      {pageTitle || 'Dashboard'}
                    </Typography>
                    {isDashboardPage && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          opacity: 0.9,
                          fontWeight: 400,
                          letterSpacing: '0.2px',
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                          background: 'linear-gradient(45deg, #ffffff 0%, #f5f5f5 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        Welcome back, {user?.firstName}! Here's your charging network overview.
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Right Section - Stats and Profile */}
                  <Stack
                    direction={{ xs: 'row', sm: 'row' }}
                    alignItems="center"
                    justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                    spacing={{ xs: 1, sm: 1.5 }}
                    sx={{ 
                      minWidth: 'fit-content',
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >

                    {/* Profile Menu */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountPopover />
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Container>
            <Outlet />
            <Footer />
          </>
        )}
      </MainStyle>
      <IntercomWidget />
    </RootStyle>
  );
}
